import { useState, useEffect, useCallback } from 'react';
import { LayoutDashboard, Trophy, LineChart, Lightbulb, ShieldCheck, Loader2, Gem } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { incentivesAPI } from '../lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import { IncentiveHeader } from '../components/incentives/IncentiveHeader';
import { HeroCountdownTimer } from '../components/incentives/HeroCountdownTimer';
import { MyPerformanceView } from '../components/incentives/MyPerformanceView';
import { TeamBoardView } from '../components/incentives/TeamBoardView';
import { DailyHistoryView } from '../components/incentives/DailyHistoryView';
import { ImprovementsView } from '../components/incentives/ImprovementsView';
import { AdminConsole } from '../components/incentives/AdminConsole';
import { PeriodManagerDialog } from '../components/incentives/PeriodManagerDialog';
import { C, fontSerif } from '../components/incentives/utils';

export default function Incentives() {
  const { user, isFounder } = useAuth();
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);
  const [settings, setSettings] = useState(null);
  const [calendar, setCalendar] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [periodId, setPeriodId] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [view, setView] = useState(null);

  const [board, setBoard] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyEmp, setHistoryEmp] = useState(null);
  const [improvements, setImprovements] = useState([]);

  // employee detail dialog
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailReport, setDetailReport] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [periodMgrOpen, setPeriodMgrOpen] = useState(false);
  const [access, setAccess] = useState(null);

  const isCoreAdmin = access ? access.is_core_admin : (isFounder() || user?.role === 'admin');
  const isAdmin = access ? access.is_admin : (isFounder() || user?.role === 'admin');
  const isManager = !!me?.employee?.is_manager;
  const canAdmin = isAdmin;
  const myId = me?.employee?.id || null;

  // ---- initial load ----
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [sRes, cRes, pRes, eRes, aRes] = await Promise.all([
          incentivesAPI.settings(),
          incentivesAPI.calendar(),
          incentivesAPI.periods(),
          incentivesAPI.employees().catch(() => ({ data: [] })),
          incentivesAPI.access().catch(() => ({ data: null })),
        ]);
        setSettings(sRes.data);
        setCalendar(cRes.data);
        setPeriods(pRes.data || []);
        setEmployees(eRes.data || []);
        setAccess(aRes.data);
        const openP = (pRes.data || []).find((p) => p.status === 'OPEN') || (pRes.data || [])[0];
        setPeriodId(openP?.id || null);

        let meData = null;
        try { const meRes = await incentivesAPI.me(openP?.id); meData = meRes.data; }
        catch { meData = null; }
        setMe(meData);
        setView(meData ? 'my' : 'board');
        if (meData) setHistoryEmp(meData.employee.id);
      } catch (e) {
        toast.error('Could not load the incentives system');
      } finally {
        setLoading(false);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- refetch me when period changes ----
  useEffect(() => {
    if (!periodId) return;
    incentivesAPI.me(periodId).then((r) => setMe(r.data)).catch(() => setMe(null));
  }, [periodId]);

  const loadBoard = useCallback(() => {
    if (!periodId) return;
    incentivesAPI.board(periodId).then((r) => setBoard(r.data)).catch(() => {});
  }, [periodId]);

  const loadHistory = useCallback(() => {
    if (!periodId) return;
    const empParam = (isAdmin || isManager) ? historyEmp : undefined;
    incentivesAPI.history(empParam, periodId).then((r) => setHistory(r.data || [])).catch(() => setHistory([]));
  }, [periodId, historyEmp, isAdmin, isManager]);

  const loadImprovements = useCallback(() => {
    incentivesAPI.improvements().then((r) => setImprovements(r.data || [])).catch(() => setImprovements([]));
  }, []);

  useEffect(() => { if (view === 'board') loadBoard(); }, [view, loadBoard]);
  useEffect(() => { if (view === 'history') loadHistory(); }, [view, loadHistory]);
  useEffect(() => { if (view === 'ideas') loadImprovements(); }, [view, loadImprovements]);

  const refreshAll = useCallback(() => {
    if (periodId) {
      incentivesAPI.me(periodId).then((r) => setMe(r.data)).catch(() => {});
      loadBoard();
      loadHistory();
    }
  }, [periodId, loadBoard, loadHistory]);

  const onPeriodSaved = useCallback((newId) => {
    incentivesAPI.periods().then((r) => {
      setPeriods(r.data || []);
      if (newId) setPeriodId(newId);
    }).catch(() => {});
    incentivesAPI.calendar().then((r) => setCalendar(r.data)).catch(() => {});
  }, []);

  const openEmployee = async (id) => {
    if (id === myId && me) { setDetailReport({ ...me }); setDetailOpen(true); return; }
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailReport(null);
    try {
      const r = await incentivesAPI.employee(id, periodId);
      const rep = r.data;
      if (board?.settings) {
        rep.reward = { amount: board.settings.reward_amount, name: board.settings.reward_name, currency: board.settings.currency };
      }
      setDetailReport(rep);
    } catch (e) {
      toast.error(e.response?.status === 403 ? 'You can only view your own detailed dashboard' : 'Could not load');
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center" data-testid="incentives-loading">
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: C.burgundy }} />
        <p className="mt-3 text-sm text-slate-500">Loading incentives…</p>
      </div>
    );
  }

  const views = [
    ...(me ? [{ id: 'my', label: 'My Dashboard', icon: LayoutDashboard }] : []),
    { id: 'board', label: 'Team Board', icon: Trophy },
    { id: 'history', label: 'History', icon: LineChart },
    { id: 'ideas', label: 'Ideas', icon: Lightbulb },
    ...(canAdmin ? [{ id: 'admin', label: 'Admin Console', icon: ShieldCheck }] : []),
  ];

  const selectedPeriod = periods.find((p) => p.id === periodId);
  const reward = me?.reward || (board?.settings ? {
    amount: board.settings.reward_amount, name: board.settings.reward_name, currency: board.settings.currency,
  } : null);
  const employeesById = Object.fromEntries((employees || []).map((e) => [e.id, e.name]));

  return (
    <div style={{ background: C.bg }} className="-m-6 min-h-screen p-6 md:-m-10 md:p-10 lg:-m-12 lg:p-12" data-testid="incentives-page">
      <IncentiveHeader
        views={views}
        activeView={view}
        onViewChange={setView}
        periods={periods}
        selectedPeriodId={periodId}
        onPeriodChange={setPeriodId}
        period={selectedPeriod}
        canManagePeriods={isAdmin}
        onManagePeriods={() => setPeriodMgrOpen(true)}
      />

      {view === 'my' && me && (
        <div className="space-y-6">
          <HeroCountdownTimer period={selectedPeriod} calendar={calendar} reward={reward} />
          <MyPerformanceView report={me} />
        </div>
      )}

      {view === 'board' && (
        <div className="space-y-6">
          <HeroCountdownTimer period={selectedPeriod} calendar={calendar} reward={reward} />
          <TeamBoardView board={board} onOpenEmployee={openEmployee} />
        </div>
      )}

      {view === 'history' && (
        <DailyHistoryView
          history={history}
          threshold={settings?.reward_threshold || 85}
          employees={employees}
          selectedId={historyEmp}
          onSelect={setHistoryEmp}
          canPick={isAdmin || isManager}
        />
      )}

      {view === 'ideas' && (
        <ImprovementsView
          improvements={improvements}
          canSubmit={!!me}
          canReview={canAdmin}
          employeesById={employeesById}
          onChanged={loadImprovements}
        />
      )}

      {view === 'admin' && canAdmin && (
        <AdminConsole
          canConfigure={isAdmin}
          isCoreAdmin={isCoreAdmin}
          access={access}
          employees={employees}
          settings={settings}
          onChanged={() => {
            incentivesAPI.settings().then((r) => setSettings(r.data));
            incentivesAPI.access().then((r) => setAccess(r.data)).catch(() => {});
            refreshAll();
          }}
          onAfterClose={refreshAll}
        />
      )}

      <PeriodManagerDialog
        open={periodMgrOpen}
        onOpenChange={setPeriodMgrOpen}
        periods={periods}
        currentPeriodId={periodId}
        onSaved={onPeriodSaved}
      />

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto bg-white sm:max-w-4xl" data-testid="employee-detail-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: fontSerif }}>
              {detailReport?.employee?.name || 'Teammate'} — {detailReport?.employee?.role_title || ''}
            </DialogTitle>
            <DialogDescription>Full performance breakdown for {selectedPeriod?.name}.</DialogDescription>
          </DialogHeader>
          {detailLoading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin" style={{ color: C.burgundy }} /></div>
          ) : detailReport ? (
            <MyPerformanceView report={detailReport} />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
